import type React from "react";
import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

interface UserInteractionContextType {
  hasUserInteracted: boolean;
}

const UserInteractionContext = createContext<
  UserInteractionContextType | undefined
>(undefined);

export const useUserInteraction = (): UserInteractionContextType => {
  const context = useContext(UserInteractionContext);
  if (!context) {
    throw new Error(
      "useUserInteraction must be used within a UserInteractionProvider",
    );
  }
  return context;
};

export const UserInteractionProvider: React.FC<{children: ReactNode}> = ({
  children,
}) => {
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  // Set up event listeners to detect user interaction
  useEffect(() => {
    const handleUserInteraction = () => {
      setHasUserInteracted(true);
    };

    // Common user interaction events
    const events = [
      "mousedown",
      "keydown",
      "touchstart",
      "pointerdown",
      "click",
    ];

    // Add event listeners
    events.forEach((event) => {
      window.addEventListener(event, handleUserInteraction, {once: true});
    });

    // Clean up event listeners
    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleUserInteraction);
      });
    };
  }, []);

  return (
    <UserInteractionContext.Provider value={{hasUserInteracted}}>
      {children}
    </UserInteractionContext.Provider>
  );
};
